import React from 'react';
import { Link } from 'react-router-dom';
import { CampaignStatus, LeadRow } from '../types';
import { CAMPAIGN_CHIP, fmtDay, fmtDayTime } from '../lib';
import { TOUCHPOINT_URL } from '../services/api';
import StagePill from './StagePill';
import PlatformLogo, { Platform } from './PlatformLogo';

/** The kit every page is built from. Flat and list-first: no boxed cards —
 *  a page is a header, a KPI row, then sections (a heading over a hairline
 *  table) separated by whitespace. Grey headers are the platform's numbers,
 *  green headers are ours. One rhythm everywhere: page `px-10 pt-8`,
 *  sections 40px apart, cells `px-3 py-3`. */

/** A floating surface — tooltips and popovers only; never a page container. */
export const popover = 'bg-warm-white border border-charcoal/10 rounded-lg shadow-[0_8px_24px_rgba(61,61,61,0.12)]';

export const btn = 'inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[13px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
export const btnTone = {
  forest: 'bg-forest text-warm-white hover:bg-forest/90',
  charcoal: 'border border-charcoal/15 text-charcoal hover:bg-charcoal/5',
  brick: 'border border-brick/30 text-brick hover:bg-brick/5',
};

/** Page frame: title (with an optional badge — a platform's logo, or our
 *  own surface's line icon, the same one the sidebar shows), a one-clause subline, a right-side
 *  action, the date bar, then sections. */
export const PageFrame: React.FC<{
  title: React.ReactNode;
  badge?: { platform: Platform } | { icon: React.ReactNode };
  chip?: React.ReactNode;
  subline?: React.ReactNode;
  action?: React.ReactNode;
  back?: { to: string; label: string };
  bar?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, badge, chip, subline, action, back, bar, children }) => (
  <div className="w-full max-w-[1480px] px-10 pt-8 pb-20">
    {back && <BackLink to={back.to}>{back.label}</BackLink>}
    <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
      <div className="flex items-center gap-3.5 min-w-0">
        {badge && ('platform' in badge
          ? <span className="w-10 h-10 rounded-xl grid place-items-center bg-warm-white border border-charcoal/10 flex-none">
              <PlatformLogo platform={badge.platform} className="w-[22px] h-[22px]" />
            </span>
          : <span className="w-10 h-10 rounded-xl grid place-items-center bg-sage/30 text-forest flex-none">{badge.icon}</span>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-[26px] font-semibold tracking-tight leading-tight">{title}</h1>
            {chip}
          </div>
          {subline && <div className="text-graphite text-[13px] mt-0.5">{subline}</div>}
        </div>
      </div>
      {action && <div className="flex gap-2">{action}</div>}
    </div>
    {bar}
    <div className="space-y-10">{children}</div>
  </div>
);

const BackLink: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => (
  <Link to={to} className="inline-flex items-center gap-1 text-graphite hover:text-forest font-semibold text-[13px] mb-4">
    <span aria-hidden>←</span> {children}
  </Link>
);

/** Page-level loading / error states. */
export const Loading: React.FC<{ what: string }> = ({ what }) => (
  <div className="flex items-center gap-2.5 text-graphite text-[13px] py-10">
    <span className="w-4 h-4 rounded-full border-2 border-sage border-t-forest animate-spin" />Loading {what}…
  </div>
);
export const Failed: React.FC<{ what: string; error: string }> = ({ what, error }) => (
  <div className="w-full px-10 py-10">
    <div className="rounded-lg bg-brick/5 text-brick px-4 py-3 text-[13.5px]">Couldn't load {what}: {error}</div>
  </div>
);

export const Pill: React.FC<{ tone?: 'ok' | 'warn' | 'gold'; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${
    tone === 'ok' ? 'bg-sage/30 text-forest'
      : tone === 'warn' ? 'bg-brick/10 text-brick'
      : tone === 'gold' ? 'bg-gold/15 text-amber-ink'
      : 'bg-charcoal/[0.06] text-graphite'}`}>{children}</span>
);

/** A broadcast campaign's lifecycle status, as a pill. */
export const StatusChip: React.FC<{ status: CampaignStatus }> = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${CAMPAIGN_CHIP[status]}`}>
    {status}
  </span>
);

export const Out: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a href={href} target="_blank" rel="noreferrer"
     className="font-semibold text-[#2c6479] hover:underline underline-offset-2 whitespace-nowrap">
    {children} ↗
  </a>
);

/** A section: heading + at most one short right-aligned caption (`note`)
 *  or control, then its table straight on the page. `pad` gives non-table
 *  children breathing room under the heading. */
export const Section: React.FC<{ title?: string; note?: React.ReactNode; pad?: boolean; children: React.ReactNode }> = ({ title, note, pad, children }) => (
  <section>
    {title && (
      <div className="flex items-end justify-between gap-3 pb-2.5 flex-wrap">
        <h2 className="font-display text-[17px] font-semibold tracking-tight">{title}</h2>
        {note && <span className="text-graphite text-xs">{note}</span>}
      </div>
    )}
    <div className={`overflow-x-auto border-t border-charcoal/15 ${pad ? 'pt-4' : ''}`}>{children}</div>
  </section>
);

/** The page's KPI row, sitting straight under the header. */
export const Strip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="pb-1">{children}</div>
);

/** The one line under a table that carries a fact the numbers can't. */
export const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="px-3 pt-3 text-[12px] text-graphite">{children}</div>
);

/** One segmented control for every in-section switch (Keywords / Ads,
 *  Device / Time of day…), shaped like the date bar's presets. */
export function Tabs<K extends string>({ value, onChange, items }: {
  value: K; onChange: (k: K) => void; items: [K, React.ReactNode][];
}) {
  return (
    <span className="inline-flex rounded-lg bg-charcoal/[0.06] p-0.5">
      {items.map(([k, label]) => (
        <button key={k} onClick={() => onChange(k)}
                className={`px-3 py-1 rounded-md text-[12.5px] font-semibold whitespace-nowrap transition-colors ${
                  value === k ? 'bg-warm-white text-charcoal shadow-[0_1px_2px_rgba(61,61,61,0.15)]' : 'text-graphite hover:text-charcoal'}`}>{label}</button>
      ))}
    </span>
  );
}

const th = 'text-[11px] uppercase tracking-[0.06em] font-semibold px-3 pt-3 pb-2.5 border-b border-charcoal/10 whitespace-nowrap';
export const td = 'px-3 py-3 whitespace-nowrap num';
export const tdBig = `${td} text-right font-semibold text-[14.5px]`;
export const tdOurs = `${tdBig} text-forest`;
export const tr = 'border-b border-charcoal/[0.06] last:border-b-0 align-top';
export const trClick = (on: boolean) => `${tr} cursor-pointer transition-colors ${on ? 'bg-sage/20' : 'hover:bg-charcoal/[0.025]'}`;

/** The selected row's forest bar — on the row's first cell. */
export const picked = (on: boolean) => (on ? 'shadow-[inset_3px_0_0_theme(colors.forest)]' : '');

export const Th: React.FC<{ ours?: boolean; left?: boolean; children: React.ReactNode }> = ({ ours, left, children }) => (
  <th className={`${th} ${left ? 'text-left' : 'text-right'} ${ours ? 'text-forest' : 'text-graphite'}`}>{children}</th>
);

/** Table empty state — one quiet line. */
export const Empty: React.FC<{ cols: number; children: React.ReactNode }> = ({ cols, children }) => (
  <tr><td colSpan={cols} className="text-center text-graphite text-[13px] py-10">{children}</td></tr>
);

export const DAYPARTS: { label: string; has: (h: number) => boolean }[] = [
  { label: 'Morning · 6–12', has: (h) => h >= 6 && h < 12 },
  { label: 'Afternoon · 12–5', has: (h) => h >= 12 && h < 17 },
  { label: 'Evening · 5–10', has: (h) => h >= 17 && h < 22 },
  { label: 'Night · 10–6', has: (h) => h >= 22 || h < 6 },
];

/** A lead's name (opens Touchpoint) over its number. */
export const LeadCell: React.FC<{ id: string; name: string; sub?: React.ReactNode }> = ({ id, name, sub }) => (
  <td className="px-3 py-2.5 whitespace-nowrap">
    <a href={`${TOUCHPOINT_URL}/leads/${id}`} target="_blank" rel="noreferrer" className="font-semibold text-charcoal hover:text-forest hover:underline underline-offset-2">{name}</a>
    <div className="text-[11px] text-graphite">{sub}</div>
  </td>
);

/** "3 done · 2 sched", or a dash. */
export const VisitsCell: React.FC<{ done: number; sched: number }> = ({ done, sched }) => (
  <td className={`${td} text-right`}>
    {done || sched
      ? <span className="text-forest font-semibold text-[14.5px]">{done}<span className="font-normal text-graphite text-[11.5px]"> done · {sched} sched</span></span>
      : <Dash />}
  </td>
);

export const Dash = () => <span className="text-charcoal/25">—</span>;

export const advisorOf = (l: LeadRow): string | null =>
  (l.assignments.find((a) => a.stage === 'presales') ?? l.assignments[0])?.user_name ?? null;

/** The leads panel a platform page ends with. `origin` is the platform's
 *  own column (campaign / post); hidden while one is selected. */
export const PlatformLeadsCard: React.FC<{
  title: string; leads: LeadRow[] | null; rows: LeadRow[]; empty: string;
  origin?: { header: string; of: (l: LeadRow) => React.ReactNode };
}> = ({ title, leads, rows, empty, origin }) => (
  <Section title={title} note={leads ? `${rows.length} lead${rows.length === 1 ? '' : 's'}` : 'loading…'}>
    <table className="w-full min-w-[860px] border-collapse">
      <thead><tr>
        <Th left>Lead</Th><Th left>Came in</Th>{origin && <Th left>{origin.header}</Th>}<Th left>Stage</Th><Th left>Advisor</Th><Th left>Next</Th>
      </tr></thead>
      <tbody>
        {leads && rows.length === 0 && <Empty cols={6}>{empty}</Empty>}
        {rows.map((l) => (
          <tr key={l.id} className={tr}>
            <LeadCell id={l.id} name={l.name} sub={l.mobile ?? ''} />
            <td className="px-3 py-2.5 whitespace-nowrap text-graphite">{fmtDay(l.created_at)}</td>
            {origin && <td className="px-3 py-2.5 text-graphite text-[12.5px] max-w-[260px] truncate">{origin.of(l)}</td>}
            <td className="px-3 py-2.5 whitespace-nowrap"><StagePill stage={l.stage} /></td>
            <td className="px-3 py-2.5 whitespace-nowrap">{advisorOf(l) ?? <Dash />}</td>
            <td className="px-3 py-2.5 whitespace-nowrap text-[12.5px]">
              {l.next_action_at
                ? <>{l.next_action_type ? `${l.next_action_type} · ` : ''}{fmtDayTime(l.next_action_at)}</>
                : <Dash />}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </Section>
);
